/**
 * Time Tracking tab — clock in/out, manual entry, timeline.
 * Fixed field name mappings to match backend serializer.
 */

import { useCallback, useEffect, useState } from 'react';
import { format, parseISO, differenceInSeconds } from 'date-fns';
import { fetchTimeEntries, createManualEntry, editTimeEntry, deleteTimeEntry, fetchJobTimeSummary, fetchUsers } from '../../../api/timeTracking';
import { clockIn, clockOut, getActiveClock, updateJob } from '../../../api/jobs';
import { Button, LoadingSpinner, EmptyState, Modal } from '../../common';
import { useAuth } from '../../../context/AuthContext';

// Labor types for manual entry dropdown. V2-0 K2: testing/installation
// removed from the selectable list per Kevin's request. Historical
// TimeEntry rows with those types keep aggregating normally; the
// enum and status choices remain in the backend for those rows.
const USER_LABOR_TYPES = [
  { value: 'standard', label: 'Standard Labor' },
];

// V2-a2 (partial): NEW selections are limited to the 2 current design
// categories. Only used for the manual-entry + clock-in selects below.
// Existing entries render via `entry.labor_type_display` provided by
// the backend serializer (see lines ~475 breakdown, ~526 per-entry
// history), so legacy labor_type values still show their proper label.
const ADMIN_DESIGN_LABOR_TYPES = [
  { value: 'complete_system_drawing', label: 'Complete System Drawing' },
  { value: 'plc_programming', label: 'PLC Programming' },
];

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function fmtTime(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'h:mm a'); } catch { return d; }
}

function fmtHours(h) {
  if (h == null) return '—';
  return `${Number(h).toFixed(1)}h`;
}

function formatTimer(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Generate time options in 15-minute intervals
function generateTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const value = `${hour}:${minute}`;
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const amPm = h < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${minute} ${amPm}`;
      options.push({ value, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

export default function TimeTrackingTab({ job, onRefresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMode, setManualMode] = useState('hours'); // 'hours' or 'timeRange'
  const [manualForm, setManualForm] = useState({
    date: '', hours: '', notes: '', labor_type: 'standard',
    start_time: '08:00', end_time: '09:00', for_user_id: '',
  });
  const [users, setUsers] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingEntryObj, setEditingEntryObj] = useState(null);
  const [editForm, setEditForm] = useState({
    mode: 'hours', // 'hours' or 'timeRange'
    hours: '', notes: '', date: '',
    start_time: '08:00', end_time: '09:00',
  });
  const [activeClock, setActiveClock] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  
  // Clock-in/out modals
  const [clockInModalOpen, setClockInModalOpen] = useState(false);
  const [clockOutModalOpen, setClockOutModalOpen] = useState(false);
  const [clockInForm, setClockInForm] = useState({
    usePreviousTime: false,
    date: new Date().toISOString().split('T')[0],
    time: '08:00',
    laborType: 'standard',
  });
  const [clockOutForm, setClockOutForm] = useState({
    usePreviousTime: false,
    date: new Date().toISOString().split('T')[0],
    time: '17:00',
    notes: '',
  });

  // Editing projected labor
  const [editingLabor, setEditingLabor] = useState(false);
  const [projectedLabor, setProjectedLabor] = useState(job.projected_labor_hours || '');

  const loadData = useCallback(async () => {
    try {
      const [entryData, summaryData, clockData] = await Promise.all([
        fetchTimeEntries({ job: job.id }),
        fetchJobTimeSummary(job.id),
        getActiveClock(),
      ]);
      setEntries(entryData);
      setSummary(summaryData);
      if (clockData && clockData.job === job.id) {
        setActiveClock(clockData);
      } else {
        setActiveClock(null);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Running timer
  useEffect(() => {
    if (!activeClock?.clock_in_at) { setElapsed(0); return; }
    const clockInTime = new Date(activeClock.clock_in_at);
    const update = () => setElapsed(Math.max(0, Math.floor(differenceInSeconds(new Date(), clockInTime))));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activeClock?.clock_in_at]);

  async function handleClockIn() {
    setActionLoading('clockin');
    try {
      const options = { laborType: clockInForm.laborType };
      
      // If user selected a previous time
      if (clockInForm.usePreviousTime) {
        const customStartTime = `${clockInForm.date}T${clockInForm.time}:00`;
        options.customStartTime = customStartTime;
      }
      
      const result = await clockIn(job.id, options);
      setActiveClock(result);
      setClockInModalOpen(false);
      setClockInForm({
        usePreviousTime: false,
        date: new Date().toISOString().split('T')[0],
        time: '08:00',
        laborType: 'standard',
      });
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Clock-in failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleClockOut() {
    setActionLoading('clockout');
    try {
      const options = { notes: clockOutForm.notes };
      
      // If user selected a previous time
      if (clockOutForm.usePreviousTime) {
        const customEndTime = `${clockOutForm.date}T${clockOutForm.time}:00`;
        options.customEndTime = customEndTime;
      }
      
      await clockOut(job.id, options);
      setActiveClock(null);
      setClockOutModalOpen(false);
      setClockOutForm({
        usePreviousTime: false,
        date: new Date().toISOString().split('T')[0],
        time: '17:00',
        notes: '',
      });
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Clock-out failed');
    } finally {
      setActionLoading(null);
    }
  }

  // Load users for admin dropdown
  useEffect(() => {
    if (isAdmin && manualOpen) {
      fetchUsers().then(setUsers).catch(() => setUsers([]));
    }
  }, [isAdmin, manualOpen]);

  async function handleManualEntry() {
    if (!manualForm.date) { alert('Please select a date'); return; }
    
    setActionLoading('manual');
    try {
      const payload = {
        job: job.id,
        notes: manualForm.notes,
        labor_type: manualForm.labor_type,
      };

      if (manualMode === 'hours') {
        // Hours mode
        const hrs = parseFloat(manualForm.hours);
        if (!hrs || hrs <= 0) { alert('Enter valid hours'); setActionLoading(null); return; }
        payload.hours = hrs;
        payload.manual_date = manualForm.date;
      } else {
        // Time range mode - construct ISO datetime strings
        const startDT = `${manualForm.date}T${manualForm.start_time}:00`;
        const endDT = `${manualForm.date}T${manualForm.end_time}:00`;
        if (manualForm.end_time <= manualForm.start_time) {
          alert('End time must be after start time');
          setActionLoading(null);
          return;
        }
        payload.start_time = startDT;
        payload.end_time = endDT;
      }

      // Admin entering time for another user
      if (isAdmin && manualForm.for_user_id) {
        payload.for_user_id = manualForm.for_user_id;
      }

      await createManualEntry(payload);
      setManualOpen(false);
      setManualForm({
        date: '', hours: '', notes: '', labor_type: 'standard',
        start_time: '08:00', end_time: '09:00', for_user_id: '',
      });
      setManualMode('hours');
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to add entry');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleEditEntry() {
    if (!editingEntry) return;
    setActionLoading('edit');
    try {
      const payload = { notes: editForm.notes };
      
      if (editForm.mode === 'timeRange' && editForm.date && editForm.start_time && editForm.end_time) {
        // Time range mode
        if (editForm.end_time <= editForm.start_time) {
          alert('End time must be after start time');
          setActionLoading(null);
          return;
        }
        payload.clock_in_at = `${editForm.date}T${editForm.start_time}:00`;
        payload.clock_out_at = `${editForm.date}T${editForm.end_time}:00`;
      } else {
        // Hours mode
        payload.manual_hours = parseFloat(editForm.hours) || undefined;
      }
      
      await editTimeEntry(editingEntry, payload);
      setEditingEntry(null);
      setEditingEntryObj(null);
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Edit failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDeleteEntry() {
    if (!editingEntry) return;
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    setActionLoading('delete');
    try {
      await deleteTimeEntry(editingEntry);
      setEditingEntry(null);
      setEditingEntryObj(null);
      loadData();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSaveProjectedLabor() {
    try {
      await updateJob(job.id, { projected_labor_hours: parseFloat(projectedLabor) || null });
      setEditingLabor(false);
      onRefresh?.();
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  }

  if (loading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>;
  }

  const isComplete = job.status === 'complete' || job.status === 'archived';
  const isClocked = !!activeClock;

  // Group entries by user - using actual serializer field names
  const byUser = {};
  entries.forEach((e) => {
    const key = e.user_display || e.user || 'Unknown';
    if (!byUser[key]) byUser[key] = [];
    byUser[key].push(e);
  });

  return (
    <div className="space-y-4">
      {/* Clock controls — prominent */}
      {!isComplete && (
        <div className={`rounded-lg border-2 px-4 py-4 ${isClocked ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              {isClocked ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-lg font-mono font-bold text-green-700">{formatTimer(elapsed)}</span>
                  </div>
                  <span className="text-sm text-green-600">Clocked in</span>
                </>
              ) : (
                <span className="text-sm text-gray-500">Not clocked in</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isClocked ? (
                <button
                  onClick={() => {
                    setClockOutForm({
                      usePreviousTime: false,
                      date: new Date().toISOString().split('T')[0],
                      time: '17:00',
                      notes: '',
                    });
                    setClockOutModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white shadow-md transition-all disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                  Clock Out
                </button>
              ) : (
                <button
                  onClick={() => {
                    setClockInForm({
                      usePreviousTime: false,
                      date: new Date().toISOString().split('T')[0],
                      time: '08:00',
                      laborType: 'standard',
                    });
                    setClockInModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-green-600 hover:bg-green-700 text-white shadow-md transition-all disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Clock In
                </button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setManualOpen(true)}>+ Manual Entry</Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary card */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-500">Total Hours:</span>{' '}
              <span className="font-semibold text-foreground">{fmtHours(summary?.total_hours)}</span>
            </div>
            <div>
              <span className="text-gray-500">Projected/Unit:</span>{' '}
              {editingLabor ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="number"
                    step="0.25"
                    className="w-20 border border-blue-300 rounded px-1.5 py-0.5 text-sm"
                    value={projectedLabor}
                    onChange={(e) => setProjectedLabor(e.target.value)}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                  />
                  <button onClick={handleSaveProjectedLabor} className="text-green-600 text-xs font-medium">Save</button>
                  <button onClick={() => setEditingLabor(false)} className="text-gray-400 text-xs">Cancel</button>
                </span>
              ) : (
                <span className="font-semibold text-foreground">
                  {fmtHours(job.projected_labor_hours || summary?.suggested_hours)}
                  <button
                    onClick={() => { setProjectedLabor(job.projected_labor_hours || summary?.suggested_hours || ''); setEditingLabor(true); }}
                    className="ml-1 text-blue-500 text-xs hover:text-blue-600"
                    title="Edit projected labor hours per unit"
                  >
                    ✎
                  </button>
                </span>
              )}
            </div>
            <div>
              <span className="text-gray-500">Total Projected:</span>{' '}
              <span className="font-semibold text-foreground">
                {job.total_projected_labor_hours
                  ? fmtHours(job.total_projected_labor_hours)
                  : (job.projected_labor_hours || summary?.suggested_hours)
                    ? fmtHours((job.projected_labor_hours || summary?.suggested_hours) * job.quantity)
                    : '—'}
                <span className="text-xs text-gray-400 ml-1">({job.quantity} unit{job.quantity !== 1 ? 's' : ''})</span>
              </span>
            </div>
            {summary?.total_hours != null && summary?.suggested_hours > 0 && (
              <div>
                <span className="text-gray-500">Utilization:</span>{' '}
                <span className={`font-semibold ${
                  (summary.total_hours / summary.suggested_hours) > 1.1 ? 'text-red-600' :
                  (summary.total_hours / summary.suggested_hours) > 0.9 ? 'text-green-600' :
                  'text-yellow-600'
                }`}>
                  {Math.round((summary.total_hours / summary.suggested_hours) * 100)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Labor Type Breakdown */}
      {summary?.labor_type_breakdown && summary.labor_type_breakdown.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Labor Utilization by Type</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {summary.labor_type_breakdown.map((lt) => (
              <div key={lt.labor_type} className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-xs text-gray-500 truncate" title={lt.labor_type_display}>
                  {lt.labor_type_display}
                </div>
                <div className="text-lg font-bold text-foreground">
                  {fmtHours(lt.hours)}
                </div>
                {summary.total_hours > 0 && (
                  <div className="text-[10px] text-gray-400">
                    {Math.round((parseFloat(lt.hours) / parseFloat(summary.total_hours)) * 100)}%
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time entries grouped by user */}
      {entries.length === 0 ? (
        <EmptyState title="No time entries" description="Clock in to start tracking time, or add a manual entry." />
      ) : (
        Object.entries(byUser).map(([user, userEntries]) => (
          <div key={user} className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
              <span className="text-sm font-medium text-foreground">{user}</span>
              <span className="ml-2 text-xs text-gray-500">
                {fmtHours(userEntries.reduce((sum, e) => sum + (parseFloat(e.duration_hours) || 0), 0))} total
              </span>
            </div>
            <div className="divide-y divide-gray-100">
              {userEntries.map((entry) => (
                <div key={entry.id} className="px-4 py-2 flex items-center justify-between text-sm hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500 w-24">
                      {fmtDate(entry.manual_date || entry.clock_in_at)}
                    </span>
                    {entry.clock_in_at && (
                      <span className="text-gray-600">
                        {fmtTime(entry.clock_in_at)} – {entry.clock_out_at ? fmtTime(entry.clock_out_at) : (
                          <span className="text-green-600 font-medium">running...</span>
                        )}
                      </span>
                    )}
                    <span className="font-medium">{fmtHours(entry.duration_hours || entry.manual_hours)}</span>
                    {entry.entry_type === 'manual' && (
                      <span className="text-[10px] text-blue-600 uppercase">manual</span>
                    )}
                    {entry.edited && (
                      <span className="text-[10px] text-orange-500 uppercase">edited</span>
                    )}
                    {entry.labor_type && entry.labor_type !== 'standard' && (
                      <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded uppercase">
                        {entry.labor_type_display || entry.labor_type}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs truncate max-w-[200px]">{entry.notes || ''}</span>
                    {!isComplete && (
                      <button
                        onClick={() => {
                          setEditingEntry(entry.id);
                          setEditingEntryObj(entry);
                          // Parse existing times if available
                          let entryDate = '';
                          let startTime = '08:00';
                          let endTime = '09:00';
                          if (entry.clock_in_at) {
                            const dt = parseISO(entry.clock_in_at);
                            entryDate = format(dt, 'yyyy-MM-dd');
                            startTime = format(dt, 'HH:mm');
                          } else if (entry.manual_date) {
                            entryDate = entry.manual_date;
                          }
                          if (entry.clock_out_at) {
                            endTime = format(parseISO(entry.clock_out_at), 'HH:mm');
                          }
                          setEditForm({
                            mode: (entry.clock_in_at && entry.clock_out_at) ? 'timeRange' : 'hours',
                            hours: entry.duration_hours || entry.manual_hours || '',
                            notes: entry.notes || '',
                            date: entryDate,
                            start_time: startTime,
                            end_time: endTime,
                          });
                        }}
                        className="text-blue-600 hover:text-blue-700 text-xs"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Manual entry modal */}
      <Modal open={manualOpen} onClose={() => setManualOpen(false)} title="Add Manual Time Entry" maxWidth="max-w-lg">
        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setManualMode('hours')}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                manualMode === 'hours'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Enter Hours
            </button>
            <button
              onClick={() => setManualMode('timeRange')}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                manualMode === 'timeRange'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Select Time Range
            </button>
          </div>

          {/* Date picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={manualForm.date}
              onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
            />
          </div>

          {manualMode === 'hours' ? (
            /* Hours mode */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours (15-min increments)</label>
              <input
                type="number"
                step="0.25"
                min="0.25"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g., 2.5 for 2 hours 30 minutes"
                value={manualForm.hours}
                onChange={(e) => setManualForm({ ...manualForm, hours: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Enter hours in 0.25 increments (0.25 = 15 min, 0.5 = 30 min, etc.)</p>
            </div>
          ) : (
            /* Time range mode */
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={manualForm.start_time}
                  onChange={(e) => setManualForm({ ...manualForm, start_time: e.target.value })}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={manualForm.end_time}
                  onChange={(e) => setManualForm({ ...manualForm, end_time: e.target.value })}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* Duration preview */}
              {manualForm.start_time && manualForm.end_time && manualForm.end_time > manualForm.start_time && (
                <div className="col-span-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                  Duration: <strong>
                    {(() => {
                      const [sh, sm] = manualForm.start_time.split(':').map(Number);
                      const [eh, em] = manualForm.end_time.split(':').map(Number);
                      const mins = (eh * 60 + em) - (sh * 60 + sm);
                      const hrs = Math.floor(mins / 60);
                      const rmins = mins % 60;
                      return `${hrs}h ${rmins}m`;
                    })()}
                  </strong>
                </div>
              )}
            </div>
          )}

          {/* Admin: Select user */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enter Time For</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={manualForm.for_user_id}
                onChange={(e) => setManualForm({ ...manualForm, for_user_id: e.target.value })}
              >
                <option value="">Myself ({user?.first_name || user?.email})</option>
                {users.filter(u => u.id !== user?.id).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">As admin, you can enter time for other users</p>
            </div>
          )}

          {/* Labor type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Labor Type</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={manualForm.labor_type}
              onChange={(e) => setManualForm({ ...manualForm, labor_type: e.target.value })}
            >
              {USER_LABOR_TYPES.map((lt) => (
                <option key={lt.value} value={lt.value}>{lt.label}</option>
              ))}
              {/* V2-0 K2/A6: design labor bookable on any job. */}
              {isAdmin && (
                <optgroup label="Design Labor">
                  {ADMIN_DESIGN_LABOR_TYPES.map((lt) => (
                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
              placeholder="Optional notes"
              value={manualForm.notes}
              onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="ghost" size="sm" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button size="sm" loading={actionLoading === 'manual'} onClick={handleManualEntry}>Add Entry</Button>
          </div>
        </div>
      </Modal>

      {/* Edit entry modal */}
      <Modal open={!!editingEntry} onClose={() => { setEditingEntry(null); setEditingEntryObj(null); }} title="Edit Time Entry" maxWidth="max-w-lg">
        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setEditForm({ ...editForm, mode: 'hours' })}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                editForm.mode === 'hours'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Enter Hours
            </button>
            <button
              onClick={() => setEditForm({ ...editForm, mode: 'timeRange' })}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                editForm.mode === 'timeRange'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Select Time Range
            </button>
          </div>

          {/* Date picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={editForm.date}
              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
            />
          </div>

          {editForm.mode === 'hours' ? (
            /* Hours mode */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours (15-min increments)</label>
              <input
                type="number"
                step="0.25"
                min="0.25"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g., 2.5 for 2 hours 30 minutes"
                value={editForm.hours}
                onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Enter hours in 0.25 increments (0.25 = 15 min, 0.5 = 30 min, etc.)</p>
            </div>
          ) : (
            /* Time range mode */
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* Duration preview */}
              {editForm.start_time && editForm.end_time && editForm.end_time > editForm.start_time && (
                <div className="col-span-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                  Duration: <strong>
                    {(() => {
                      const [sh, sm] = editForm.start_time.split(':').map(Number);
                      const [eh, em] = editForm.end_time.split(':').map(Number);
                      const mins = (eh * 60 + em) - (sh * 60 + sm);
                      const hrs = Math.floor(mins / 60);
                      const rmins = mins % 60;
                      return `${hrs}h ${rmins}m`;
                    })()}
                  </strong>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
              placeholder="Optional notes"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            {editingEntryObj?.entry_type === 'manual' ? (
              <Button variant="danger" size="sm" loading={actionLoading === 'delete'} onClick={handleDeleteEntry}>
                Delete Entry
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setEditingEntry(null); setEditingEntryObj(null); }}>Cancel</Button>
              <Button size="sm" loading={actionLoading === 'edit'} onClick={handleEditEntry}>Save</Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Clock In modal */}
      <Modal open={clockInModalOpen} onClose={() => setClockInModalOpen(false)} title="Clock In" maxWidth="max-w-md">
        <div className="space-y-4">
          {/* Previous time toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="usePreviousTimeIn"
              checked={clockInForm.usePreviousTime}
              onChange={(e) => setClockInForm({ ...clockInForm, usePreviousTime: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="usePreviousTimeIn" className="text-sm text-gray-700">
              I forgot to clock in — select a previous start time
            </label>
          </div>

          {/* Date and time (shown when using previous time) */}
          {clockInForm.usePreviousTime && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={clockInForm.date}
                  onChange={(e) => setClockInForm({ ...clockInForm, date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={clockInForm.time}
                  onChange={(e) => setClockInForm({ ...clockInForm, time: e.target.value })}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Labor type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Labor Type</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={clockInForm.laborType}
              onChange={(e) => setClockInForm({ ...clockInForm, laborType: e.target.value })}
            >
              {USER_LABOR_TYPES.map((lt) => (
                <option key={lt.value} value={lt.value}>{lt.label}</option>
              ))}
              {/* V2-0 K2/A6: design labor bookable on any job. */}
              {isAdmin && (
                <optgroup label="Design Labor">
                  {ADMIN_DESIGN_LABOR_TYPES.map((lt) => (
                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="ghost" size="sm" onClick={() => setClockInModalOpen(false)}>Cancel</Button>
            <Button size="sm" loading={actionLoading === 'clockin'} onClick={handleClockIn}>
              {clockInForm.usePreviousTime ? 'Clock In (Backdated)' : 'Clock In Now'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Clock Out modal */}
      <Modal open={clockOutModalOpen} onClose={() => setClockOutModalOpen(false)} title="Clock Out" maxWidth="max-w-md">
        <div className="space-y-4">
          {/* Show current clock-in info */}
          {activeClock?.clock_in_at && (
            <div className="text-sm text-gray-600 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
              Clocked in at: <strong>{fmtTime(activeClock.clock_in_at)}</strong> on {fmtDate(activeClock.clock_in_at)}
            </div>
          )}

          {/* Previous time toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="usePreviousTimeOut"
              checked={clockOutForm.usePreviousTime}
              onChange={(e) => setClockOutForm({ ...clockOutForm, usePreviousTime: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="usePreviousTimeOut" className="text-sm text-gray-700">
              I forgot to clock out — select a previous end time
            </label>
          </div>

          {/* Date and time (shown when using previous time) */}
          {clockOutForm.usePreviousTime && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={clockOutForm.date}
                  onChange={(e) => setClockOutForm({ ...clockOutForm, date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={clockOutForm.time}
                  onChange={(e) => setClockOutForm({ ...clockOutForm, time: e.target.value })}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* Duration preview */}
              {activeClock?.clock_in_at && (() => {
                try {
                  const clockInDt = parseISO(activeClock.clock_in_at);
                  const clockInDate = format(clockInDt, 'yyyy-MM-dd');
                  const clockInTime = format(clockInDt, 'HH:mm');
                  // Calculate duration if same day
                  if (clockOutForm.date === clockInDate && clockOutForm.time > clockInTime) {
                    const [sh, sm] = clockInTime.split(':').map(Number);
                    const [eh, em] = clockOutForm.time.split(':').map(Number);
                    const mins = (eh * 60 + em) - (sh * 60 + sm);
                    const hrs = Math.floor(mins / 60);
                    const rmins = mins % 60;
                    return (
                      <div className="col-span-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md">
                        Duration: <strong>{hrs}h {rmins}m</strong>
                      </div>
                    );
                  }
                } catch { /* ignore */ }
                return null;
              })()}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
              placeholder="Optional notes about this work session"
              value={clockOutForm.notes}
              onChange={(e) => setClockOutForm({ ...clockOutForm, notes: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="ghost" size="sm" onClick={() => setClockOutModalOpen(false)}>Cancel</Button>
            <Button size="sm" variant="danger" loading={actionLoading === 'clockout'} onClick={handleClockOut}>
              {clockOutForm.usePreviousTime ? 'Clock Out (Backdated)' : 'Clock Out Now'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
